import type { SendMessageBatchRequest, SendMessageBatchRequestEntry } from '@aws-sdk/client-sqs';
import { SQS } from '@aws-sdk/client-sqs';
import { awaitAllSafely, batch, batchByMaxPayloadSize } from '@weft/utils';
import type { MessageOptions } from '..';
import { MAX_BATCH_SIZE, MAX_MESSAGE_PAYLOAD_SIZE as MAX_SQS_MESSAGE_SIZE } from '../sns/sns-consts';

const sqs = new SQS();

export async function sendBatchMessages<Payload>(payloadObjects: Payload[], queueUrl: string, options: MessageOptions = {}) {
  const sendBatchInputs = buildSendBatchInputs(payloadObjects, queueUrl, options);

  const sendingMessages = sendBatchInputs.map(sendBatchInput => sqs.sendMessageBatch(sendBatchInput));

  const sendResults = await awaitAllSafely(...sendingMessages);

  console.info(`Sent ${sendResults.length} messages to queue ${queueUrl}`);

  return sendResults;
}

function buildSendBatchInputs<Payload>(payloadObjects: Payload[], queueUrl: string, options: MessageOptions = {}) {
  const entries = payloadObjects.map((payloadObject, payloadIndex) => {
    const id = payloadIndex.toString();

    return buildSendBatchRequestEntry(payloadObject, id, options);
  });

  const quantityLimitedBatches = batch(entries, MAX_BATCH_SIZE);
  const sizeLimitedBatches = quantityLimitedBatches.flatMap(entriesBatch => batchByMaxPayloadSize(entriesBatch, MAX_SQS_MESSAGE_SIZE, JSON.stringify));

  return sizeLimitedBatches.map(entriesBatch => buildSendMessageBatchRequest(queueUrl, entriesBatch));
}

function buildSendBatchRequestEntry<Payload>(payloadObject: Payload, id: string, options: MessageOptions = {}): SendMessageBatchRequestEntry {
  const { dedupeId, groupId } = options;

  const message = JSON.stringify(payloadObject);

  return {
    Id: id,
    MessageBody: message,
    MessageDeduplicationId: dedupeId,
    MessageGroupId: groupId,
  };
}

function buildSendMessageBatchRequest(queueUrl: string, entries: SendMessageBatchRequestEntry[]): SendMessageBatchRequest {
  return {
    QueueUrl: queueUrl,
    Entries: entries,
  };
}
